import { useCallback, useLayoutEffect, useRef, useState } from 'react'

interface IndicatorPos {
  top: number
  height: number
}

interface SlidingIndicatorResult {
  containerRef: React.RefObject<HTMLDivElement | null>
  setItemRef: (key: string, el: HTMLElement | null) => void
  indicatorStyle: React.CSSProperties | null
}

/**
 * Tracks the position of an active item within a container and returns
 * styles for a floating indicator that slides between items.
 */
export function useSlidingIndicator(activeKey: string | undefined): SlidingIndicatorResult {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [pos, setPos] = useState<IndicatorPos | null>(null)
  const hasAnimated = useRef(false)

  const setItemRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) itemRefs.current.set(key, el)
    else itemRefs.current.delete(key)
  }, [])

  useLayoutEffect(() => {
    if (!activeKey || !containerRef.current) {
      setPos(null)
      hasAnimated.current = false
      return
    }

    const el = itemRefs.current.get(activeKey)
    if (!el) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()

    setPos({
      top: elRect.top - containerRect.top,
      height: elRect.height,
    })

    // Enable transitions only after the first measurement so the pill
    // doesn't animate in from nowhere on initial render
    if (!hasAnimated.current) {
      requestAnimationFrame(() => {
        hasAnimated.current = true
      })
    }
  }, [activeKey])

  const indicatorStyle: React.CSSProperties | null = pos
    ? {
        top: pos.top,
        height: pos.height,
        transition: hasAnimated.current ? 'top 0.15s cubic-bezier(0.4, 0, 0.2, 1), height 0.1s ease' : 'none',
      }
    : null

  return { containerRef, setItemRef, indicatorStyle }
}
